/**
 * 视觉证据卡片组件
 * 展示 AI 检测的热力图/包围盒和置信度
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6
 */

import React from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Eye, CheckCircle, AlertCircle, XCircle } from "lucide-react";
import type {
  VisualEvidenceCardProps,
  VisualEvidence,
} from "@/types/conversion-funnel";
import {
  getConfidenceColor,
  CONFIDENCE_THRESHOLDS,
} from "@/types/conversion-funnel";

/**
 * 置信度颜色配置
 */
const confidenceColorConfig = {
  green: {
    text: "text-green-700",
    bg: "bg-green-100",
    border: "border-green-300",
    indicator: "bg-green-500",
  },
  yellow: {
    text: "text-amber-700",
    bg: "bg-amber-100",
    border: "border-amber-300",
    indicator: "bg-amber-500",
  },
  red: {
    text: "text-red-700",
    bg: "bg-red-100",
    border: "border-red-300",
    indicator: "bg-red-500",
  },
};

/**
 * 置信度图标组件
 */
function ConfidenceIcon({ color }: { color: "green" | "yellow" | "red" }) {
  const iconClass = "h-4 w-4";
  switch (color) {
    case "green":
      return <CheckCircle className={cn(iconClass, "text-green-500")} />;
    case "yellow":
      return <AlertCircle className={cn(iconClass, "text-amber-500")} />;
    case "red":
      return <XCircle className={cn(iconClass, "text-red-500")} />;
  }
}

/**
 * 视觉证据卡片
 * 展示原图缩略图、AI 检测包围盒、置信度和视觉特征列表
 */
export function VisualEvidenceCard({
  imageUrl,
  conditionDisplay,
  confidence,
  visualEvidence,
  className,
}: VisualEvidenceCardProps) {
  const { t } = useTranslation("assessments");
  const confidenceColor = getConfidenceColor(confidence);
  const colorConfig = confidenceColorConfig[confidenceColor];

  return (
    <div
      className={cn(
        "rounded-xl border bg-white p-4 shadow-sm",
        "flex flex-col h-full",
        className
      )}
    >
      {/* 卡片标题 */}
      <div className="flex items-center gap-2 mb-3">
        <Eye className="h-5 w-5 text-blue-500" />
        <h3 className="text-sm font-semibold text-gray-700">
          {t("conversion.visualEvidence.title", "视觉证据")}
        </h3>
      </div>

      {/* 图片区域（带包围盒叠加） */}
      <div className="relative mb-3 rounded-lg overflow-hidden bg-gray-100">
        <img
          src={imageUrl}
          alt={conditionDisplay}
          className="w-full h-32 object-cover"
        />
        {/* AI 检测包围盒叠加 */}
        {visualEvidence.map((evidence, index) => (
          <BoundingBox key={index} evidence={evidence} />
        ))}
      </div>

      {/* 置信度显示 */}
      <div
        className={cn(
          "flex items-center justify-between p-2 rounded-lg mb-3",
          colorConfig.bg,
          colorConfig.border,
          "border"
        )}
      >
        <div className="flex items-center gap-2">
          <ConfidenceIcon color={confidenceColor} />
          <span className="text-sm text-gray-600">
            {t("conversion.visualEvidence.confidence", "AI 置信度")}
          </span>
        </div>
        <span className={cn("text-lg font-bold", colorConfig.text)}>
          {confidence}%
        </span>
      </div>

      {/* 视觉特征列表 */}
      <div className="flex-1">
        <h4 className="text-xs font-medium text-gray-500 mb-2">
          {t("conversion.visualEvidence.detectedSigns", "检测到的视觉特征")}
        </h4>
        <ul className="space-y-1.5">
          {visualEvidence.length > 0 ? (
            visualEvidence.map((evidence, index) => (
              <li
                key={index}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                <span>
                  {evidence.body_part && (
                    <span className="text-gray-500">
                      {evidence.body_part}:{" "}
                    </span>
                  )}
                  {evidence.sign}
                </span>
              </li>
            ))
          ) : (
            <li className="text-sm text-gray-400 italic">
              {t("conversion.visualEvidence.noSigns", "未检测到明显特征")}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/**
 * 包围盒组件
 * 在图片上叠加 AI 检测的区域
 */
interface BoundingBoxProps {
  evidence: VisualEvidence;
}

function BoundingBox({ evidence }: BoundingBoxProps) {
  // 如果没有坐标信息，不渲染包围盒
  if (evidence.bbox_x === undefined || evidence.bbox_y === undefined) {
    return null;
  }

  return (
    <div
      className="absolute border-2 border-red-500 rounded pointer-events-none"
      style={{
        left: `${evidence.bbox_x}%`,
        top: `${evidence.bbox_y}%`,
        width: "20%",
        height: "20%",
        transform: "translate(-50%, -50%)",
      }}
    >
      {/* 标签 */}
      <span className="absolute -top-5 left-0 bg-red-500 text-white text-xs px-1 rounded whitespace-nowrap">
        {evidence.sign}
      </span>
    </div>
  );
}

export default VisualEvidenceCard;
